import {
  messengerChatNewMessageDividerId,
  messengerChatScrollBarId,
  messengerChatScrollBarThreshold,
} from '@widgets/messenger/const'
import scrollbarService from '@widgets/shared/services/scrollbarService'
import { useChatStore } from '@widgets/messenger/stores/useChatStore'
import { throttle } from 'lodash-es'
import { computed } from '@/composition/vue/compositionApi'

export const useChatScroll = () => {
  const {
    currentScrollPosition,
    lastScrollPosition,
    setCurrentScrollPosition,
    hasNotViewedNewMessages,
    setNewMessageStateAsViewed,
    isNewMessagesInsideView,
    setIsNewMessagesInsideView,
  } = useChatStore()

  const getScrollState = () => {
    return scrollbarService.getState(messengerChatScrollBarId)
  }

  const scrollListener = () => {
    const state = getScrollState()
    const clientHeight = state?.clientHeight ?? 0
    const scrollTop = state?.scrollTop ?? 0
    const atBottom = (state?.scrollBottom || 0) <= messengerChatScrollBarThreshold
    const newMessageDivider = document.getElementById(messengerChatNewMessageDividerId)

    if (newMessageDivider) {
      setIsNewMessagesInsideView(scrollTop + clientHeight > newMessageDivider.offsetTop)
    } else {
      setIsNewMessagesInsideView(false)
    }

    setCurrentScrollPosition({
      scrollTop,
      atBottom,
    })
    if (atBottom && hasNotViewedNewMessages.value) {
      setNewMessageStateAsViewed()
    }
  }

  const throttledScrollListener = throttle(scrollListener, 200)

  const isScrollButtonShown = computed(() => !currentScrollPosition.value.atBottom && !isNewMessagesInsideView.value)

  const onScrollButtonClick = () => {
    if (hasNotViewedNewMessages.value) {
      scrollbarService.scrollToElement(`#${messengerChatNewMessageDividerId}`, {
        behavior: 'smooth',
      })
    } else {
      scrollbarService.scrollToBottom(messengerChatScrollBarId, 'smooth')
    }
  }

  const scrollToBottom = () => {
    scrollbarService.scrollToBottom(messengerChatScrollBarId)
  }

  const scrollBy = (delta: number) => {
    const state = getScrollState()
    if (!state) {
      return
    }
    scrollbarService.scroll(messengerChatScrollBarId, { top: state.scrollTop + delta })
  }

  const mountScroll = () => {
    scrollbarService.addScrollListener(messengerChatScrollBarId, throttledScrollListener)

    if (!lastScrollPosition.value) {
      scrollbarService.scrollToBottom(messengerChatScrollBarId)
    } else if (lastScrollPosition.value.atBottom && hasNotViewedNewMessages.value) {
      scrollbarService.scrollToElement(`#${messengerChatNewMessageDividerId}`)
    } else if (lastScrollPosition.value.atBottom) {
      scrollbarService.scrollToBottom(messengerChatScrollBarId)
    } else {
      scrollbarService.scroll(messengerChatScrollBarId, { top: lastScrollPosition.value.scrollTop })
    }
    scrollListener() // Init scroll position in the store if scroll event is not fired
  }

  const unmountScroll = () => {
    scrollbarService.removeScrollListener(messengerChatScrollBarId, throttledScrollListener)
  }

  return {
    isScrollButtonShown,
    getScrollState,
    scrollToBottom,
    scrollBy,
    onScrollButtonClick,
    mountScroll,
    unmountScroll,
  }
}
